import $http from '../../shared/http';
import {
  downloadFileFromResponse,
  getServiceStatus,
  getServiceTitle,
  serviceDateWithOrdinal,
  serviceTitle,
} from '../../shared/utils';

export const services = {
  namespaced: true,
  state: () => {
    return {
      servicesForSelect: null,
      servicesByDateSelect: null,
      serviceCreated: false,
      newServiceID: null,
      serviceRegisterData: null,
      currentService: null,
      previousService: null,
      attendanceRecorded: false,
      attendanceRecordData: null,
      attendancePreviouslyCaptured: false,
      serviceEdited: false,
      editServiceID: null,
      serviceAttendanceSummary: null,
      attendanceUploadIDs: null,
      currentMonthServices: null,
      servicesForNavigation: null,
      todayServices: null,
    };
  },
  getters: {
    getTodayServices: (state) => {
      const services = state.todayServices;
      if (services && services.length) {
        return services.map((service) => {
          const serviceTypeStartTime = service.start_time;
          const serviceTypeEndTime = service.end_time;

          const status = getServiceStatus({
            startTime: serviceTypeStartTime,
            endTime: serviceTypeEndTime,
          });

          return {
            serviceName: serviceTitle(service),
            serviceType: service.service_type,
            serviceDate: service.service_date,
            serviceId: service.id,
            ...status,
            serviceTypeStartTime,
            serviceTypeEndTime,
          };
        });
      } else {
        return null;
      }
    },

    getTodaysOngoingServices: (state, getters) => {
      if (getters.getTodayServices && getters.getTodayServices.length) {
        return getters.getTodayServices.filter((service) => service.ongoing);
      } else return null;
    },

    getServicesForSelect: (state) => state.servicesForSelect,
    getServicesByDateSelect: (state) => state.servicesByDateSelect,
    getServiceRegisterData: (state) => state.serviceRegisterData,
    getNewServiceID: (state) => state.newServiceID,
    isServiceCreated: (state) => state.serviceCreated,
    getEditServiceID: (state) => state.editServiceID,
    isServiceEdited: (state) => state.serviceEdited,
    getCurrentService: (state) => state.currentService,
    getCurrentServiceStatus: (state) => {
      const service = state.currentService;
      // The backend has already computed the start and end times
      const startTime = service?.start_time;
      const endTime = service?.end_time;
      return getServiceStatus({ startTime, endTime });
    },
    isCurrentServiceGivingEnabled: (state, getters, rootState, rootGetters) => {
      return Boolean(
        rootGetters['service_types/getCurrentServiceServiceType']?.enable_giving ?? false
      );
    },
    isCurrentServiceHeadCountsEnabled: (state, getters, rootState, rootGetters) => {
      return Boolean(
        rootGetters['service_types/getCurrentServiceServiceType']?.enable_head_counts ?? false
      );
    },
    isCurrentServiceAttendanceCaptureEnabled: (state, getters, rootState, rootGetters) => {
      return Boolean(
        rootGetters['service_types/getCurrentServiceServiceType']?.enable_attendance_capture ??
          false
      );
    },
    isCurrentServiceSchedulingEnabled: (state, getters, rootState, rootGetters) => {
      return Boolean(
        rootGetters['service_types/getCurrentServiceServiceType']?.enable_scheduling ?? false
      );
    },
    isCurrentServiceServiceActivitiesTemplateEnabled: (state, getters) => {
      return Boolean(getters.getCurrentService?.serviceActivitiesTemplate?.is_enabled ?? false);
    },
    getPreviousService: (state) => state.previousService,
    getCurrentServiceID: (state) => (state.currentService ? state.currentService.id : null),
    getCurrentServiceTypeName: (state, getters) => {
      return getters.getCurrentService && getters.getCurrentService.serviceType
        ? getters.getCurrentService.serviceType.service_type_name
        : '';
    },
    getCurrentServiceDateWithOrdinal: (state) => {
      return serviceDateWithOrdinal(state.currentService);
    },
    getCurrentServiceTitle: (state, getters) => {
      return serviceTitle(getters.getCurrentService);
    },
    getServiceHeaderNavServices: (state) => state.servicesForNavigation,
    isAttendanceRecorded: (state) => state.attendanceRecorded,
    isAttendancePreviouslyCaptured: (state) => state.attendancePreviouslyCaptured,
    getAttendanceRecordData: (state) => {
      return state.attendanceRecordData;
    },
    getServiceEditData: (state) => state.currentService,
    getServiceActivitiesScheduleCollection: (state, getters) => {
      const currentService = getters.getCurrentService;
      const schedules = currentService?.serviceActivitiesSchedules ?? [];

      const serviceActivities = currentService?.serviceActivitiesTemplate?.serviceActivities ?? [];

      if (!schedules) return [];
      else {
        let collector = [];

        schedules.forEach((schedule, index) => {
          const activityName = schedule?.serviceActivity?.name ?? null;
          const activityLeaderName = schedule?.serviceActivity?.alternative_name ?? null;
          const activityID = schedule?.serviceActivity?.id ?? null;
          const activityType = schedule?.serviceActivity?.type ?? null;
          const scheduleId = schedule?.id;

          const isActivityAssigneeVisitor = schedule.is_visitor;

          let activityAssigneeName;
          if (!isActivityAssigneeVisitor) {
            activityAssigneeName = schedule?.scheduledMember?.full_name ?? null;
          } else if (schedule.visitor) {
            activityAssigneeName = schedule?.visitor?.full_name ?? null;
          } else {
            activityAssigneeName = null;
          }

          const activityAssigneesProfilePicture =
            schedule?.scheduledMember?.profile_picture_2 ?? null;

          let activityAssigneesProfileID;
          if (!isActivityAssigneeVisitor) {
            activityAssigneesProfileID = schedule?.scheduledMember?.id ?? null;
          } else {
            activityAssigneesProfileID = schedule && schedule.visitor ? schedule.visitor.id : null;
          }

          let activityAssigneesID;
          if (!isActivityAssigneeVisitor) {
            activityAssigneesID = activityAssigneesProfileID ?? null;
          } else {
            activityAssigneesID = schedule && schedule.visitor ? schedule.visitor.id : null;
          }

          const activitySortOrder = serviceActivities
            .filter((activity) => activity.meta.pivot_service_activity_id === activityID)
            .reduce((acc, cur) => acc + cur.meta.pivot_sort_order, 0);

          // Populate collector array
          if (activityType === 'Individual') {
            collector[index] = {
              name: activityName,
              alternativeName: activityLeaderName,
              type: activityType,
              id: activityID,
              sortOrder: activitySortOrder,
              is_visitor: isActivityAssigneeVisitor,
              profile_picture: activityAssigneesProfilePicture,
              profile_id: activityAssigneesProfileID,
              scheduleId: scheduleId,
              add_notes: schedule.add_notes,
              notes: schedule.notes,
              assign_position_members: schedule.assign_position_members,
              assign_group_members: schedule.assign_group_members,
              group: schedule.group,
              position: schedule.position,
            };
            collector[index].assignees = activityAssigneesID;
            collector[index].assignees_names = activityAssigneeName;
          } else {
            // Check if activity ID value already exists in the collector array
            let isCollectorActivityIDExists;
            let indexOfExistingActivityIDInCollectorArray;
            if (collector && collector.length) {
              collector.forEach((collectorActivity, collectorIndex) => {
                const { type: collectorActivityType, id: collectorActivityID } = collectorActivity;

                if (collectorActivityType !== 'Individual' && collectorActivityID === activityID) {
                  isCollectorActivityIDExists = true;
                  indexOfExistingActivityIDInCollectorArray = collectorIndex;
                }
              });

              // if assignee array does not exist, assign values at the current index of the activities array
              if (!isCollectorActivityIDExists) {
                collector[index] = {
                  name: activityName,
                  alternativeName: activityLeaderName,
                  type: activityType,
                  id: activityID,
                  sortOrder: activitySortOrder,
                  add_notes: schedule.add_notes,
                  notes: schedule.notes,
                  assign_position_members: schedule.assign_position_members,
                  assign_group_members: schedule.assign_group_members,
                  group: schedule.group,
                  position: schedule.position,
                };
                collector[index].assignees = [];
                collector[index].assignees.push(activityAssigneesID);
                collector[index].assignees_names = [];
                collector[index].assignees_names.push(activityAssigneeName);
                collector[index].profile_picture = [];
                collector[index].profile_picture.push(activityAssigneesProfilePicture);
                collector[index].profile_id = [];
                collector[index].profile_id.push(activityAssigneesProfileID);
                collector[index].scheduleId = [];
                collector[index].scheduleId.push(scheduleId);
              } else {
                // else assign values at the index of the collector array where the same assignee existed
                if (typeof indexOfExistingActivityIDInCollectorArray === 'number') {
                  collector[indexOfExistingActivityIDInCollectorArray].assignees.push(
                    activityAssigneesID
                  );
                  collector[indexOfExistingActivityIDInCollectorArray].assignees_names.push(
                    activityAssigneeName
                  );
                  collector[indexOfExistingActivityIDInCollectorArray].profile_picture.push(
                    activityAssigneesProfilePicture
                  );
                  collector[indexOfExistingActivityIDInCollectorArray].profile_id.push(
                    activityAssigneesProfileID
                  );
                  collector[indexOfExistingActivityIDInCollectorArray].scheduleId.push(scheduleId);
                }
              }
            } else {
              collector[index] = {
                name: activityName,
                alternativeName: activityLeaderName,
                type: activityType,
                id: activityID,
                sortOrder: activitySortOrder,
                add_notes: schedule.add_notes,
                notes: schedule.notes,
                assign_position_members: schedule.assign_position_members,
                assign_group_members: schedule.assign_group_members,
                group: schedule.group,
                position: schedule.position,
              };
              collector[index].assignees = [];
              collector[index].assignees.push(activityAssigneesID);
              collector[index].assignees_names = [];
              collector[index].assignees_names.push(activityAssigneeName);
              collector[index].profile_picture = [];
              collector[index].profile_picture.push(activityAssigneesProfilePicture);
              collector[index].profile_id = [];
              collector[index].profile_id.push(activityAssigneesProfileID);
              collector[index].scheduleId = [];
              collector[index].scheduleId.push(scheduleId);
            }
          }
        });
        collector = collector.filter((item) => item).sort((a, b) => a.sortOrder - b.sortOrder);
        return collector;
      }
    },

    getAttendanceUploadIDs: (state) => state.attendanceUploadIDs,
    getAttendanceUploadIDsArray: (state, getters) => {
      if (getters['getAttendanceUploadIDs']) {
        return (
          getters.getAttendanceUploadIDs
            .trim()
            // Convert space-separated strings to array
            .split(' ')
            // Ensure there is no empty string
            .filter((id) => !!id)
            // Return only numbers from the array
            .filter((id) => /^[0-9]*$/.test(id))
        );
      } else return null;
    },
    getServiceAttendanceSummary: (state) => state.serviceAttendanceSummary,
    getCurrentMonthServices: (state) => state.currentMonthServices,
  },
  mutations: {
    setCurrentMonthServices: (state, payload) => (state.currentMonthServices = payload),
    setTodayServices: (state, payload) => (state.todayServices = payload),
    setServicesForSelect: (state, payload) => (state.servicesForSelect = payload),
    setServicesByDateSelect: (state, payload) => (state.servicesByDateSelect = payload),
    initialiseServiceForForm: (state) => {
      state.servicesForSelect = null;
      state.servicesByDateSelect = null;
    },
    clearRegisterResponseData: (state) => {
      state.serviceCreated = null;
      state.newServiceID = null;
    },
    clearAttendanceRecordResponseData: (state) => {
      state.attendanceRecorded = null;
    },
    initialiseAttendanceRecordData: (state) => {
      state.attendanceRecordData = [];
    },
    setServiceCreated: (state, payload) => (state.serviceCreated = payload),
    setServiceEdited: (state, payload) => (state.serviceEdited = payload),
    setAttendanceRecorded: (state, payload) => (state.attendanceRecorded = payload),
    setNewServiceID: (state, payload) => (state.newServiceID = payload),
    setEditServiceID: (state, payload) => (state.editServiceID = payload),
    setServiceRegisterData: (state, payload) => (state.serviceRegisterData = payload),
    setCurrentService: (state, data) => {
      state.currentService = data;
    },
    setServicesForNavigation: (state, payload) => {
      state.servicesForNavigation = {
        prev: !payload.prevService
          ? null
          : { ...payload.prevService, name: serviceTitle(payload.prevService) },
        next: !payload.nextService
          ? null
          : { ...payload.nextService, name: serviceTitle(payload.nextService) },
      };
    },
    setPreviousService: (state, data) => (state.previousService = data),
    setAttendanceRecordData: (state, data) => {
      state.attendanceRecordData = data;
    },
    setAttendancePreviouslyCaptured: (state, payload) => {
      state.attendancePreviouslyCaptured = payload;
    },
    setServiceEditData: (state, payload) => {
      state.currentService = payload;
    },
    setServiceAttendanceSummary: (state, payload) => {
      state.serviceAttendanceSummary = payload;
    },
    setAttendanceUploadIDs: (state, payload) => {
      state.attendanceUploadIDs = payload;
    },
  },
  actions: {
    async loadServicesForSelect({ commit }) {
      return $http()
        .get('/servicesForSelect')
        .then((response) => {
          commit('setServicesForSelect', response.data.data);
          return response;
        });
    },

    async loadServicesByDateSelect({ commit }, { service_date, churchId }) {
      commit('setServicesByDateSelect', null);
      return $http('v2')
        .get(`organisations/${churchId}/services`, {
          params: {
            service_date,
          },
        })
        .then((response) => {
          commit(
            'setServicesByDateSelect',
            response.data.data.map((service) => ({
              value: service.id,
              text: getServiceTitle(service.meta.service_type, service.service_date),
            }))
          );
          return response;
        });
    },

    async createServiceAction({ commit, getters }, { churchId }) {
      commit('clearRegisterResponseData');
      return $http()
        .post(`organisations/${churchId}/services`, getters['getServiceRegisterData'], {
          notifiable: true,
        })
        .then(async (response) => {
          commit('setNewServiceID', response.data.data.id);
          commit('setServiceCreated', true);
          return response;
        })
        .catch((error) => {
          commit('clearRegisterResponseData');
          return Promise.reject(error);
        });
    },
    async massServicesCreationAction(ctx, form) {
      const churchId = ctx.rootGetters['auth/inStoreResolvedOrgId']();
      ctx.commit('clearRegisterResponseData');
      return $http('v2')
        .post(`organisations/${churchId}/services/mass_creation`, form, {
          notifiable: true,
        })
        .catch((error) => {
          ctx.commit('clearRegisterResponseData');
          return Promise.reject(error);
        });
    },
    async SAVE_SERVICE_DETAILS(ctx, { form, serviceId }) {
      const churchId = ctx.rootGetters['auth/inStoreResolvedOrgId']();
      return $http('v2').put(`/organisations/${churchId}/services/${serviceId}`, form, {
        notifiable: true,
      });
    },
    async loadCurrentService({ commit }, { churchId, serviceId }) {
      return $http('v2')
        .get(`organisations/${churchId}/services/${serviceId}`)
        .then((response) => {
          commit('setCurrentService', response.data.data.currentService);
          commit('setPreviousService', response.data.data.previousWeekService);
          commit('setServicesForNavigation', response.data.data);
          return response;
        })
        .catch((error) => {
          if (error?.response?.status === 404) {
            commit('setCurrentService', null);
          }
          return Promise.reject(error?.response ?? error);
        });
    },

    async recordAttendanceAction({ commit, getters, rootGetters }) {
      commit('clearAttendanceRecordResponseData');
      commit('setAttendanceRecorded', false);

      const churchId = rootGetters['auth/inStoreResolvedOrgId']();
      const { service_id, ...payload } = getters['getAttendanceRecordData'];

      return $http('v2')
        .post(`organisations/${churchId}/services/${service_id}/attendances`, payload, {
          timeout: 120000,
          notifiable: true,
        })
        .then(async (response) => {
          commit('setAttendanceRecorded', true);
          return response;
        })
        .catch((error) => {
          commit('clearAttendanceRecordResponseData');
          return Promise.reject(error?.response ?? error);
        });
    },

    async uploadAttendanceAction({ commit, getters, rootGetters }, service_id) {
      commit('clearAttendanceRecordResponseData');
      commit('setAttendanceRecorded', false);

      const churchId = rootGetters['auth/inStoreResolvedOrgId']();

      return $http('v2')
        .post(
          `organisations/${churchId}/services/${service_id}/attendances`,
          { membership_ids: getters.getAttendanceUploadIDsArray },
          {
            timeout: 120000,
            notifiable: true,
          }
        )
        .then(async (response) => {
          commit('setAttendanceRecorded', true);
          return response;
        })
        .catch((error) => {
          commit('clearAttendanceRecordResponseData');
          return Promise.reject(error?.response ?? error);
        });
    },
    async massMarkAsAttendedAction(ctx, { serviceId, profileIds, churchId, isDeletion = false }) {
      const resolvedChurchId = churchId ?? ctx.rootGetters['auth/inStoreResolvedOrgId']();

      return $http('v2').post(
        `organisations/${resolvedChurchId}/services/${serviceId}/${
          !isDeletion ? 'attendances' : 'delete_attendances'
        }`,
        { profile_ids: profileIds },
        {
          timeout: 120000,
          notifiable: true,
        }
      );
    },
    async editAttendanceAction({ commit, getters }) {
      commit('clearAttendanceRecordResponseData');
      commit('setAttendanceRecorded', false);
      return await $http()
        .patch('/service/attendance/edit', getters['getAttendanceRecordData'], {
          timeout: 20000,
          notifiable: true,
        })
        .then(async (response) => {
          commit('setAttendanceRecorded', true);
          return response;
        })
        .catch((error) => {
          commit('clearAttendanceRecordResponseData');
          return Promise.reject(error?.response ?? error);
        });
    },
    async checkPreviousAttendanceCapture({ commit }, id) {
      commit('setAttendancePreviouslyCaptured', false);
      return await $http()
        .get('/service/attendance/check', {
          params: {
            service_id: id,
          },
        })
        .then(async (response) => {
          commit('setAttendancePreviouslyCaptured', response.data.data);
          return response;
        })
        .catch((error) => {
          commit('setAttendancePreviouslyCaptured', false);
          return Promise.reject(error?.response ?? error);
        });
    },

    async loadServiceEditData({ commit }, id) {
      commit('setServiceEditData', null);

      return $http()
        .get(`/services/${id}/edit`)
        .then(async (response) => {
          commit('setServiceEditData', response.data.data.currentService);
          commit('setPreviousService', response.data.data.previousWeekService);
          return response;
        });
    },

    async loadServiceAttendanceSummary({ commit }, { churchId, serviceId }) {
      return $http('v2')
        .get(`/organisations/${churchId}/services/${serviceId}/attendance_summary`)
        .then(async (response) => {
          commit('setServiceAttendanceSummary', response.data.data);
          return response;
        });
    },

    async checkServiceDateServiceTypeUniquenessAction(_, payload) {
      return $http().get('/service/check', {
        params: payload,
      });
    },
    async deleteService(ctx, { id }) {
      return $http().delete(`/services/${id}`, { notifiable: true });
    },
    async dissociateActivitiesTemplate(ctx, service_id) {
      return $http().delete(`/services/${service_id}/dissociate-template`, {
        notifiable: true,
      });
    },

    async COPY_SCHEDULE_FROM_PREVIOUS_SERVICE(ctx, { service_id, activity_id, churchId }) {
      return $http('v2')
        .get(
          `organisations/${churchId}/services/${service_id}/service_activities_schedules/${activity_id}/copy
        `,
          {
            notifiable: true,
          }
        )
        .then(async (response) => {
          return response.data.data;
        });
    },

    async SAVE_SERVICE_HEAD_COUNTS(ctx, { form, serviceId, churchId }) {
      return $http('v2')
        .post(`organisations/${churchId}/services/${serviceId}/head_counts`, form, {
          notifiable: true,
        })
        .then(async (response) => {
          return response.data.data;
        });
    },

    async SAVE_SERVICE_GIVINGS(ctx, { form, serviceId }) {
      return $http()
        .put(`/services/${serviceId}/givings`, form, { notifiable: true })
        .then(async (response) => {
          return response.data.data;
        })
        .catch((error) => {
          return Promise.reject(error?.response ?? error);
        });
    },

    async SAVE_SERVICE_SCHEDULE(ctx, { form, serviceId, churchId }) {
      return $http('v2')
        .post(
          `organisations/${churchId}/services/${serviceId}/service_activities_schedules`,
          form,
          {
            notifiable: true,
          }
        )
        .then(async (response) => {
          return response.data.data;
        })
        .catch((error) => {
          return Promise.reject(error?.response ?? error);
        });
    },

    async PRINT_SERVICE_ABSENTEES(ctx, { churchId, query, serviceId }) {
      return $http('v2')
        .get(`organisations/${churchId}/prints/service_absentees/${serviceId}`, {
          responseType: 'blob',
          params: query,
          canBeImpersonated: true,
        })
        .then(async (response) => {
          return await downloadFileFromResponse(response, 'roster');
        });
    },
  },
};
